import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/Field";
import { OrderList } from "@/components/OrderList";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { regionGroups } from "@/lib/conference";

function RegionName({ region, onSaved }) {
  const [name, setName] = useState(region.name);
  const save = async () => {
    if (!name.trim() || name.trim() === region.name) { setName(region.name); return; }
    try { await api.put(`/regions/${region.id}`, { name }); toast.success("Region renamed"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} className="h-9 bg-white text-slate-900" data-testid={`region-name-${region.id}`} />;
}

function ChurchRow({ church, regions, onMove }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="min-w-[8rem] flex-1 truncate font-semibold text-slate-900">{church.name}</span>
      <NativeSelect value={church.region_id || ""} onChange={(v) => onMove(church, v)} placeholder="Unassigned" options={regions.map((r) => ({ value: r.id, label: r.name }))} className="h-9 w-44" testid={`church-region-${church.id}`} />
    </div>
  );
}

export function AdminRegions() {
  const { regions, churches, reload } = useApp();
  const [name, setName] = useState("");
  const groups = regionGroups(regions, churches);
  const run = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const add = (e) => { e.preventDefault(); if (!name.trim()) return; run(() => api.post("/regions", { name }), "Region added").then(() => setName("")); };
  const move = (c, rid) => run(() => api.put(`/churches/${c.id}/region`, { region_id: rid || null }), "Church moved");
  const reorderChurches = (key, ids) => {
    const all = groups.flatMap((g) => (g.key === key ? ids : g.churches.map((c) => c.id)));
    run(() => api.put("/churches/reorder", { ids: all }), "Order saved");
  };
  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-regions">
      <div>
        <h3 className="font-heading text-lg font-bold text-slate-900">Regions</h3>
        <p className="text-sm text-slate-500">Create regions, put them in order, and choose each church's region. Churches can be ordered inside each region.</p>
      </div>
      <form onSubmit={add} className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Region 1" maxLength={80} className="h-10 bg-white text-slate-900" data-testid="region-add-input" />
        <Button type="submit" data-testid="region-add-btn"><Plus className="mr-1 h-4 w-4" /> Add region</Button>
      </form>
      <OrderList items={regions} onSave={(ids) => run(() => api.put("/regions/reorder", { ids }), "Region order saved")} testid="region" empty="No regions yet."
        renderItem={(r) => (
          <div className="flex items-center gap-2">
            <RegionName region={r} onSaved={reload} />
            <ConfirmDelete onConfirm={() => run(() => api.delete(`/regions/${r.id}`), "Region deleted")} title={`Delete ${r.name}?`} message="Its churches become Unassigned. No records are deleted." testid={`region-delete-${r.id}`} />
          </div>
        )} />
      <div className="grid gap-4 lg:grid-cols-2">
        {groups.map((g) => (
          <div key={g.key} className="space-y-2 rounded-xl bg-[hsl(var(--sand))] p-3" data-testid={`region-group-${g.key}`}>
            <div className="font-semibold text-slate-800">{g.name} <span className="text-sm font-normal text-slate-500">({g.churches.length})</span></div>
            <OrderList items={g.churches} onSave={(ids) => reorderChurches(g.key, ids)} testid={`region-${g.key}-church`} empty="No churches in this region."
              renderItem={(c) => <ChurchRow church={c} regions={regions} onMove={move} />} />
          </div>
        ))}
      </div>
    </section>
  );
}
